// Disclosure panel: inline description editor + AI summary block for a hosted site row

import { Sparkles, Pencil, RefreshCw, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { DESCRIPTION_MAX } from './utils'
import type { HostedSite } from '../../hooks/use-hosted-sites'

interface SiteDisclosureProps {
  site: HostedSite
  isDark: boolean
  isEditing: boolean
  draft: string
  setDraft: (v: string) => void
  summaryFull: boolean
  setSummaryFull: (fn: (v: boolean) => boolean) => void
  pendingSummaryId: string | null
  summaryError: { id: string; message: string } | null
  onStartEdit: (site: HostedSite) => void
  onCancelEdit: () => void
  onCommitEdit: (site: HostedSite) => void
  onRegenerate: (id: string) => void
}

export function SiteDisclosure({
  site,
  isDark,
  isEditing,
  draft,
  setDraft,
  summaryFull,
  setSummaryFull,
  pendingSummaryId,
  summaryError,
  onStartEdit,
  onCancelEdit,
  onCommitEdit,
  onRegenerate,
}: SiteDisclosureProps) {
  return (
    <div className={cn('mt-3 space-y-2 border-t pt-3', isDark ? 'border-white/[0.06]' : 'border-neutral-100')}>
      {isEditing ? (
        <input
          type="text"
          autoFocus
          value={draft}
          maxLength={DESCRIPTION_MAX}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => onCommitEdit(site)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onCommitEdit(site)
            } else if (e.key === 'Escape') {
              e.preventDefault()
              onCancelEdit()
            }
          }}
          placeholder="Description"
          className={cn(
            'w-full rounded-md border px-2 py-1 text-xs outline-none',
            isDark
              ? 'border-white/[0.1] bg-surface-2 text-neutral-200 placeholder-neutral-500 focus:border-brand-500/50'
              : 'border-neutral-200 bg-white text-neutral-800 placeholder-neutral-400 focus:border-brand-500',
          )}
        />
      ) : site.description ? (
        <button
          type="button"
          onClick={() => onStartEdit(site)}
          className={cn(
            'group/desc flex w-full items-start gap-1.5 rounded text-left text-xs leading-relaxed cursor-text',
            isDark ? 'text-neutral-300 hover:text-neutral-200' : 'text-neutral-600 hover:text-neutral-800',
          )}
          title="Click to edit"
        >
          <span className="flex-1">{site.description}</span>
          <Pencil className="mt-0.5 h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/desc:opacity-60" aria-hidden="true" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onStartEdit(site)}
          className={cn(
            'flex items-center gap-1 text-xs underline-offset-2 hover:underline',
            isDark ? 'text-brand-400' : 'text-brand-600',
          )}
        >
          <Pencil className="h-3 w-3" aria-hidden="true" />
          Add description
        </button>
      )}

      {site.summary ? (
        <div className={cn('flex gap-1.5 text-xs italic leading-relaxed', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
          <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-brand-500" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <span className={cn(!summaryFull && 'line-clamp-3')}>{site.summary}</span>
            <div className="mt-1 flex items-center gap-2 not-italic">
              {site.summary.length > 180 && (
                <button
                  type="button"
                  onClick={() => setSummaryFull((v) => !v)}
                  className={cn('underline-offset-2 hover:underline', isDark ? 'text-brand-400' : 'text-brand-600')}
                >
                  {summaryFull ? 'Show less' : 'Show more'}
                </button>
              )}
              <button
                type="button"
                onClick={() => onRegenerate(site.id)}
                disabled={pendingSummaryId === site.id}
                className={cn(
                  'flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] disabled:opacity-50',
                  isDark ? 'text-neutral-400 hover:bg-surface-3 hover:text-neutral-200' : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
                )}
                title="Regenerate summary"
                aria-label="Regenerate summary"
              >
                {pendingSummaryId === site.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
                {pendingSummaryId === site.id ? 'Regenerating…' : 'Regenerate'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onRegenerate(site.id)}
          disabled={pendingSummaryId === site.id}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs disabled:opacity-50',
            isDark ? 'bg-surface-3 text-neutral-300 hover:bg-surface-4' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
          )}
        >
          {pendingSummaryId === site.id ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5 text-brand-500" />
          )}
          {pendingSummaryId === site.id ? 'Generating summary…' : 'Generate AI summary'}
        </button>
      )}

      {summaryError?.id === site.id && (
        <p className={cn('text-xs', isDark ? 'text-red-400' : 'text-red-600')}>
          {summaryError.message}
        </p>
      )}
    </div>
  )
}
